import type { Syllable } from "@kensio/pinyinjs";

import {
  bopomofoScript,
  type InputScript,
  pinyinScript,
  writePinyinWord,
} from "./syllable-text.js";
import { systems } from "./transcriptions.js";

/**
 * What goes in and what comes out, for the romanised-input tools.
 *
 * Two lookups, both keyed by what the template says: `data-cb-tool` names the
 * input script, and `data-cb-output` names each panel's system. Keeping them
 * here rather than in the controller means the set of systems a page offers is
 * settled by the page's front matter and this table, and nowhere else.
 */

/** How an output panel writes a word, and whether it writes capitals. */
export interface Writer {
  readonly write: (syllables: readonly Syllable[]) => string;
  readonly hasCapitals: boolean;
}

const writers = new Map<string, Writer>([
  [
    "pinyin",
    {
      write: (syllables: readonly Syllable[]): string =>
        writePinyinWord(syllables, "marks"),
      hasCapitals: true,
    },
  ],
  [
    "pinyin-numbers",
    {
      write: (syllables: readonly Syllable[]): string =>
        writePinyinWord(syllables, "numbers"),
      hasCapitals: true,
    },
  ],
  ...systems.map((system): [string, Writer] => [
    system.id,
    { write: system.write, hasCapitals: system.capitals },
  ]),
]);

const scripts = new Map<string, InputScript>([
  ["pinyin", pinyinScript],
  ["bopomofo", bopomofoScript],
]);

/** How a panel writes its words, or nothing for a name this does not know. */
export function writerFor(id: string | undefined): Writer | undefined {
  return id === undefined ? undefined : writers.get(id);
}

/** How a tool reads its input, or nothing for a name this does not know. */
export function scriptFor(name: string | undefined): InputScript | undefined {
  return name === undefined ? undefined : scripts.get(name);
}
